import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaGraduationCap, FaUsers, FaHandshake, FaAward } from 'react-icons/fa';

const aboutItems = [
  {
    title: "Our Mission",
    description: "To empower students with expert guidance, enabling them to secure seats in top medical and engineering colleges across India and abroad.",
    icon: <FaGraduationCap className="text-2xl" />,
  },
  {
    title: "Our Values",
    description: "Integrity, excellence, and student success are at the core of everything we do. We strive to provide personalized guidance to every student.",
    icon: <FaUsers className="text-2xl" />,
  },
  {
    title: "Our Approach",
    description: "We combine industry knowledge, personalized counseling, and extensive college networks to help students navigate complex admission processes.",
    icon: <FaHandshake className="text-2xl" />,
  },
  {
    title: "Our Promise",
    description: "Accurate information, transparent processes, and dedicated support throughout your admission journey — with no advance payment required.",
    icon: <FaAward className="text-2xl" />,
  },
];

const About = () => {
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });
  const [counters, setCounters] = useState({ years: 0, admissions: 0, colleges: 0, satisfaction: 0 });

  useEffect(() => {
    if (!inView) return;
    const steps = 60;
    const duration = 2000;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      if (step < steps) {
        setCounters({
          years: Math.min(Math.round((15 * step) / steps), 15),
          admissions: Math.min(Math.round((5000 * step) / steps), 5000),
          colleges: Math.min(Math.round((100 * step) / steps), 100),
          satisfaction: Math.min(Math.round((98 * step) / steps), 98),
        });
        step += 1;
      } else {
        clearInterval(timer);
      }
    }, interval);
    return () => clearInterval(timer);
  }, [inView]);

  return (
    <section id="about" ref={ref} className="py-20">
      <div className="container mx-auto px-4">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="text-section font-bold font-heading text-gray-900" style={{textWrap: 'balance'}}>
            About Vaishnavi Educare
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto" style={{textWrap: 'pretty'}}>
            Your trusted education partner with over 15 years of experience helping students secure seats in top medical, ayurvedic, and engineering colleges.
          </p>
        </motion.div>

        {/* Two-column content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-14">
          {/* Photo card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="lg:col-span-2 rounded-2xl overflow-hidden border border-slate-200 shadow-sm min-h-[260px]"
          >
            <img
              src="/photos/counselling-2.jpg"
              alt="Vaishnavi Educare counselling session"
              className="w-full h-full object-cover object-center"
              style={{ minHeight: '260px' }}
            />
          </motion.div>

          {/* Expertise + stats */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.14 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 flex-1"
            >
              <h3 className="text-xl font-semibold font-heading text-gray-900 mb-4">Our Expertise</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                Vaishnavi Educare specializes in comprehensive admission guidance for medical, ayurvedic, and engineering programs across India and Nepal — helping students at every NEET score bracket, from 168 to 600+.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                Strong college networks enable confirmed seat placements through Management, NRI, and Merit quotas with full documentation support.
              </p>
              <span className="inline-flex items-center gap-2 rounded-full bg-green-50 border border-green-200 px-3 py-1 text-xs font-semibold text-green-800">
                No advance payment until seat is confirmed
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-primary-800 rounded-2xl shadow-sm p-6"
            >
              <div className="grid grid-cols-2 gap-5">
                {[
                  { val: counters.years,                  suffix: '+', label: 'Years of Experience' },
                  { val: counters.admissions.toLocaleString(), suffix: '+', label: 'Students Guided' },
                  { val: counters.colleges,               suffix: '+', label: 'Partner Colleges' },
                  { val: counters.satisfaction,           suffix: '%', label: 'Student Satisfaction' },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-2xl font-bold text-white font-heading tabular-nums">{s.val}{s.suffix}</div>
                    <div className="text-xs text-primary-200 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Values grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          {aboutItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.1 + index * 0.08 }}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col items-center text-center"
            >
              <div className="mb-4 p-3 rounded-lg bg-primary-800 text-white">
                {item.icon}
              </div>
              <h3 className="text-base font-semibold font-heading text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="text-center"
        >
          <a
            href="#contact"
            className="inline-block px-8 py-3 bg-primary-800 text-white font-medium rounded-full hover:bg-primary-900 transition-colors duration-200"
          >
            Get Free Counselling
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
